interface StorageLike {
    isLocalStorageAccessible: () => boolean;
    getItem: (key: string) => string | null;
    setItem: (key: string, value: string) => void;
    removeItem: (key: string) => void;
    clear: () => void;
}

/**
 * This util provides a safe wrapper around the localStorage API
 * in case user's browser has localStorage disabled or blocked.
 *
 * It has the same API but catches SecurityError exceptions,
 * when users have localStorage disabled or blocked.
 */
const safeLocalStorage: StorageLike = {

    /**
     * Checks if localStorage is accessible.
     */
    isLocalStorageAccessible(): boolean {
        try {
            // SSR-Safe: Only works clientside
            if (!import.meta.client) {
                return false;
            }
            const testKey = '__safeLocalStorageTest__';
            window.localStorage?.setItem(testKey, '1');
            window.localStorage?.removeItem(testKey);
            return true;
        }
        catch (e) {
            return e instanceof DOMException && e.name === 'SecurityError';
        }
    },

    getItem(key: string): string | null {
        try {
            // SSR-Safe: Only works clientside
            return import.meta.client
                ? window.localStorage?.getItem(key)
                : null;
        }
        catch (e) {
            if (e instanceof DOMException && e.name === 'SecurityError') {
                return null;
            }
            throw e;
        }
    },

    setItem(key: string, value: string): void {
        try {
            // SSR-Safe: Only works clientside
            if (import.meta.client) {
                window.localStorage?.setItem(key, value);
            }
        }
        catch (e) {
            if (!(e instanceof DOMException && e.name === 'SecurityError')) {
                throw e;
            }
        }
    },

    removeItem(key: string): void {
        try {
            // SSR-Safe: Only works clientside
            if (import.meta.client) {
                window.localStorage?.removeItem(key);
            }
        }
        catch (e) {
            if (!(e instanceof DOMException && e.name === 'SecurityError')) {
                throw e;
            }
        }
    },

    clear(): void {
        try {
            // SSR-Safe: Only works clientside
            if (import.meta.client) {
                window.localStorage?.clear();
            }
        }
        catch (e) {
            if (!(e instanceof DOMException && e.name === 'SecurityError')) {
                throw e;
            }
        }
    },
};

export default safeLocalStorage;
